import { round2, toNumber } from './orderTotals';

export const buildRefundLines = (refundOrder) => {
  if (!refundOrder) return []; // skip, if no order has been set to refund

  const items = (refundOrder.orderItems || []).map((it) => {
    // go through all orderItems of the refundOrder:
    const orderedQty = Math.max(1, Math.floor(Number(it.quantity || 1)));
    const unitGross = Math.max(0, Number(it.unitGross || 0));
    const lineGross = Math.max(0, Number(it.lineGross || 0));

    // add the current item in the following format to the orderItemsOfRefundOrder array:
    return {
      key: `item:${it._id}`,
      sourceType: 'item',
      sourceId: String(it._id),
      name: it.name || `Item ${String(it._id).slice(-6)}`,
      orderedQty,
      unitGross,
      lineGross,
    };
  });

  const shippingGross = Math.max(0, Number(refundOrder.shippingGross ?? 0)); // ensure a valid number for shippingGross

  // add the shippingGross costs as a seperate item to the orderItemsOfRefundOrder array:
  if (shippingGross > 0) {
    items.push({
      key: 'shipping',
      sourceType: 'shipping',
      sourceId: 'shipping',
      name: 'Shipping',
      orderedQty: 1, // shipping can only be refunded once
      unitGross: shippingGross, // full shipping amount
      lineGross: shippingGross, // same as unitGross because qty=1
      onlyFullRefund: true, // UI should lock this item to "full refund only"
    });
  }

  return items;
}; // computes the orderItemsOfRefundOrder array, which contains all items of the refundOrder (incl. shipping as a separate item)

export const calculateRefundAmount = (orderItemsOfRefundOrder, refundItems) => {
  let refundGrossPreview = 0;

  for (const l of orderItemsOfRefundOrder) {
    // go through all orderItemsOfRefundOrder:
    const cfg = refundItems[l.key]; // get the configuration of the current item from the refundItems array
    if (!cfg) continue; // if the current item is not part of refundItems (it is not enabled), don't add it to the refundAmount

    // if cfg is truthy, the current item has been enabled for refund:
    if (l.key === 'shipping') {
      const shipGrossAbs = round2(Number(l.unitGross || 0));
      refundGrossPreview += -shipGrossAbs; // shipping is all-or-nothing: enabled switch = full refund once
      continue;
    }

    const value = toNumber(cfg.refundValue); // get the refundValue of the current item (% or €)
    if (value <= 0) continue;

    const qty = Math.max(
      0,
      Math.min(l.orderedQty, Math.floor(toNumber(cfg.refundQty) || 0))
    ); // get the refundQuantity of the current item (the amount of times the refund of this item should be applied)
    if (qty <= 0) continue;

    const grossAbs =
      cfg.refundType === 'amount'
        ? round2(value)
        : round2(Number(l.unitGross || 0) * (value / 100)); // compute/get the per-unit refund for the current item

    const lineGross = -round2(grossAbs * qty);

    refundGrossPreview += lineGross; // add the refund of the current item to the refundGrossPreview
  }

  refundGrossPreview = round2(refundGrossPreview); // round the grossPreview to 2 decimal places

  return Math.abs(refundGrossPreview);
}; // computes the total refund amount (The calculation of refundAmount follows the exact same calculation of the refundGross in the backend. This ensures the preview matches the invoice.)

export const hasRefundableItem = (refundItems, orderItemsOfRefundOrder) =>
  Object.entries(refundItems).some(([key, cfg]) => {
    // go through all refundItems:
    const item = orderItemsOfRefundOrder.find((l) => l.key === key); // get the data of the current refundItem
    if (!item) return false;

    const valueOk = toNumber(cfg.refundValue) > 0; // check if the current item has a refundValue > 0
    const qty = Math.floor(toNumber(cfg.refundQty) || 0); // get the refundQty of the current item
    const qtyOk = qty > 0 && qty <= item.orderedQty; // check if the refundQty is valid

    return valueOk && qtyOk; // return true, if the current item has a valid refundValue and refundQty
  }); // check if at least one refund item is configured with a refund value > 0

export const buildRefundRequestItems = (orderItemsOfRefundOrder, refundItems) =>
  orderItemsOfRefundOrder
    .map((l) => {
      // go through all orderItemsOfRefundOrder:
      const cfg = refundItems[l.key]; // get the configuration of the current item from the refundItems array
      if (!cfg) return null; // if the current item is not part of refundItems, it is not part of the refund

      // if cfg is truthy, add the current item to the payload:
      const value = toNumber(cfg.refundValue); // get the refundValue of the current item (€ or %)
      if (value <= 0) return null;

      const qty = Math.max(
        0,
        Math.min(l.orderedQty, Math.floor(toNumber(cfg.refundQty) || 0))
      ); // get the refundQty of the current item
      if (qty <= 0) return null;

      // add configuration of current item to the payload:
      return {
        sourceType: l.sourceType, // 'item' or 'shipping'
        sourceId: l.sourceId, // item._id or 'shipping'
        refundType: l.key === 'shipping' ? 'amount' : cfg.refundType, // percent or amount (per unit)
        refundValue: l.key === 'shipping' ? l.unitGross : value, // total refund (per unit)
        quantity: l.key === 'shipping' ? 1 : qty,
        note: cfg.note?.trim() || '',
      };
    })
    .filter(Boolean); // the items of the refund request sent to the backend

export const normalizeRefundValue = (raw, refundType, unitGross) => {
  // allow empty input
  if (raw.trim() === '') {
    return '';
  }

  const n = toNumber(raw);

  if (refundType === 'amount') {
    // clamp fixed amount per unit to [0, unitGross]
    const clamped = Math.max(0, Math.min(n, unitGross));
    return String(clamped);
  } else {
    // percent: clamp to [0, 100]
    const clamped = Math.max(0, Math.min(n, 100));
    return String(clamped);
  }
}; // the refund value input as typed by the admin, clamped to what the refund type allows

export const initialRefundLine = (item) => {
  const isShipping = item.key === 'shipping'; // check if the enabled item is the shipping item

  // we set different values for the shipping item, because shipping can only be refunded as a whole
  // and therefor we set the final values for the shipping item already here, that cannot be edited:
  return {
    refundType: isShipping ? 'amount' : 'percent', // shipping: only fixed amount
    refundValue: isShipping ? String(item.unitGross) : '', // shipping: full shipping gross
    refundQty: isShipping ? 1 : item.orderedQty, // shipping: always 1
    note: '',
  };
}; // the settings a refund line starts with when the admin enables it (OrderListScreen's enableItem, PR 32)

export const refundLineWithPatch = (line, patch) => ({
  ...line,
  ...patch,
}); // the line's settings with an input's change applied (OrderListScreen's setItemRefund, PR 32; the start it held for a line the admin hadn't enabled went in PR 32, as the inputs only render for enabled lines)
